import { database } from '@app/firebase';
import PrimaryImage from '@components/primary-image/PrimaryImage';
import useDocument from '@hooks/useDocument';
import { ContentBlock } from '@layouts/ContentBlocks';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import shortAddress from '@ui/utils/shortAddress';
import slugOrID from '@ui/utils/slugOrID';
import staticMap from '@ui/utils/staticMap';
import { makeSeoData } from 'config/next-seo.config';
import { collection, getDocs, query as firestoreQuery, where } from 'firebase/firestore';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import { parseAddress } from 'parse-address';
import React, { useMemo } from 'react';

import { DefaultFirestoreConverter } from '../../../types/DefaultFirestoreConverter';
import useStyles from './case-studies.style';

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    // Query without orderBy first to avoid index issues, then sort in memory
    const caseStudyDocs = await getDocs(
      firestoreQuery(
        collection(database, 'homes').withConverter(new DefaultFirestoreConverter<guesthouse.Home>()),
        where('case_study', '==', true),
        where('published', '==', true)
      )
    );

    let caseStudies = caseStudyDocs.docs.map(doc => doc.data());

    // Sort in memory by install_date (descending)
    caseStudies = caseStudies.sort((a, b) => {
      const dateA = a.install_date?.seconds || a.install_date || 0;
      const dateB = b.install_date?.seconds || b.install_date || 0;

      return dateB - dateA;
    });

    console.log(`Found ${caseStudies.length} case studies`);

    return {
      props: {
        caseStudies: JSON.stringify(caseStudies),
      },
    };
  } catch (error) {
    console.error('Error fetching case studies:', error);

    // If published filter fails, try without it
    try {
      const caseStudyDocs = await getDocs(
        firestoreQuery(
          collection(database, 'homes').withConverter(new DefaultFirestoreConverter<guesthouse.Home>()),
          where('case_study', '==', true)
        )
      );

      let caseStudies = caseStudyDocs.docs.map(doc => doc.data());

      caseStudies = caseStudies.sort((a, b) => {
        const dateA = a.install_date?.seconds || a.install_date || 0;
        const dateB = b.install_date?.seconds || b.install_date || 0;

        return dateB - dateA;
      });

      console.log(`Found ${caseStudies.length} case studies (without published filter)`);

      return {
        props: {
          caseStudies: JSON.stringify(caseStudies),
        },
      };
    } catch (fallbackError) {
      console.error('Error fetching case studies (fallback):', fallbackError);
      return {
        props: {
          caseStudies: JSON.stringify([]),
        },
      };
    }
  }
};

function CaseStudiesPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { md } = useBreakpoints();
  const { theme } = useStyles();
  const caseStudies = JSON.parse(props.caseStudies) as guesthouse.Home[];

  return (
    <>
      <NextSeo
        {...makeSeoData({
          pageTitle: 'Case Studies',
          metaTitle: 'Case Studies | Guest House',
          metaDescription: 'Explore our home staging case studies to see real results from our staging services.',
        })}
      />
      
      <ContentBlock
        maxWidth={1400}
        margin={[0, 'auto']}
        padding={md ? [8, 0] : [4, 2]}
      >
        <Typography
          variant="h1"
          style={{ 
            fontSize: md ? 48 : 32, 
            textAlign: 'center',
            marginBottom: md ? theme.spacing(6) : theme.spacing(4)
          }}
        >
          Case Studies
        </Typography>

        {caseStudies.length === 0 ? (
          <Typography
            variant="body1"
            style={{ textAlign: 'center', marginTop: theme.spacing(4) }}
          >
            No case studies available at this time.
          </Typography>
        ) : (
          <Grid
            container
            spacing={md ? 3 : 2}
          >
            {caseStudies.map((home) => (
              <CaseStudyCard
                key={home.docID}
                home={home}
              />
            ))}
          </Grid>
        )}
      </ContentBlock>
    </>
  );
}

interface CaseStudyCardProps {
  home: guesthouse.Home;
}

function CaseStudyCard({ home }: CaseStudyCardProps) {
  const { classes } = useStyles();
  const { doc: homeDoc } = useDocument(`homes/${home.docID}`);

  const fullHome = useMemo(() => {
    if (homeDoc?.exists()) {
      return homeDoc.data() as guesthouse.Home;
    }
    return home;
  }, [homeDoc, home]);

  const parsedAddress = useMemo(() => {
    if (home.address) {
      return parseAddress(home.address.replace(', USA', ''));
    }
    return {
      city: '',
      state: '',
    };
  }, [home]);

  const short = useMemo(() => {
    if (home.address) {
      return shortAddress(home.address);
    }
    return '';
  }, [home]);

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
    >
      <Link
        href={`/case-studies/${slugOrID(home)}`}
        style={{
          textDecoration: 'none',
        }}
      >
        <div className={classes.card}>
          <div className={classes.imageWrapper}>
            <PrimaryImage
              item={fullHome}
              fallback={staticMap(home.address)}
              className={classes.image}
            />
          </div>
          <Typography
            variant="h3"
            className={classes.title}
          >
            {home.case_study_title || short}
          </Typography>
          {short && (
            <address
              className={classes.address}
              style={{ fontStyle: 'normal' }}
            >
              {short}
              {home.unit && `, Unit ${home.unit}`}
            </address>
          )}
          {parsedAddress?.city && parsedAddress?.state && (
            <address className={classes.cityState}>
              {`${parsedAddress.city}, ${parsedAddress.state}`}
            </address>
          )}
        </div>
      </Link>
    </Grid>
  );
}

export default CaseStudiesPage;
